import { useEffect, useState } from 'react'
import { MinusCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Collapse, Drawer, Form, Input, Space, Table, Tag, Typography, Alert, message } from 'antd'
import { adminApi, formatApiError } from '../api'
import { formatDateOrDash } from './ui'
import type { KnowledgeScope } from '../constants/knowledgeScope'

type ScopeParams = { scope: KnowledgeScope }

export type RelationRow = {
  source_entity: string
  relation: string
  target: string
}

export type EntityRow = {
  name: string
  type?: string
  properties?: Record<string, string>
}

export type ReviewItemDraft = {
  id: number
  title: string
  relations_draft_json?: {
    entities?: EntityRow[]
    relations?: RelationRow[]
    extracted_at?: string
    model?: string
    segments?: number
    segments_processed?: number
    document_chars?: number
    text_truncated?: boolean
  } | null
}

type Props = {
  open: boolean
  item: ReviewItemDraft | null
  scopeParams: ScopeParams
  onClose: () => void
  onDone: () => void
}

function draftRelations(item: ReviewItemDraft | null): RelationRow[] {
  return (item?.relations_draft_json?.relations || []).map((row) => ({
    source_entity: row.source_entity || '',
    relation: row.relation || '关联',
    target: row.target || '',
  }))
}

export function RelationsReviewDrawer({ open, item, scopeParams, onClose, onDone }: Props) {
  const [form] = Form.useForm()
  const [current, setCurrent] = useState<ReviewItemDraft | null>(item)
  const [saving, setSaving] = useState(false)
  const [extracting, setExtracting] = useState(false)

  useEffect(() => {
    setCurrent(item)
  }, [item])

  useEffect(() => {
    if (!open || !current) return
    form.setFieldsValue({
      relations: draftRelations(current),
    })
  }, [open, current, form])

  const pollDraftUpdated = (itemId: number, prevAt: string | undefined) => {
    const deadline = Date.now() + 600_000
    const tick = (): Promise<void> => {
      if (Date.now() > deadline) {
        message.info('抽取仍在进行，请稍后在审核列表查看')
        return Promise.resolve()
      }
      return adminApi
        .get<ReviewItemDraft>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
        .then((r) => {
          setCurrent(r.data)
          const nextAt = r.data.relations_draft_json?.extracted_at
          if (nextAt && nextAt !== prevAt) {
            message.success('重新抽取完成')
            return
          }
          return new Promise<void>((resolve) => {
            window.setTimeout(() => {
              void tick().then(resolve)
            }, 2000)
          })
        })
    }
    return tick()
  }

  const reextract = () => {
    if (!current) return
    const itemId = current.id
    const prevAt = current.relations_draft_json?.extracted_at
    setExtracting(true)
    adminApi
      .post<{ queued: boolean; message?: string }>(
        `/admin/knowledge/items/${itemId}/relations/extract`,
        null,
        { params: scopeParams },
      )
      .then((r) => {
        if (!r.data.queued) {
          message.info(r.data.message || '该条目已在抽取队列中')
          return
        }
        message.success('已提交后台重新抽取')
        return pollDraftUpdated(itemId, prevAt)
      })
      .catch((e) => message.error(formatApiError(e, '提交抽取失败')))
      .finally(() => setExtracting(false))
  }

  const discard = () => {
    if (!current) return
    setSaving(true)
    adminApi
      .post(`/admin/knowledge/items/${current.id}/relations/skip`, null, { params: scopeParams })
      .then(() => {
        message.info('已不采用本次抽取')
        onDone()
      })
      .catch((e) => message.error(formatApiError(e, '操作失败')))
      .finally(() => setSaving(false))
  }

  const confirm = () => {
    if (!current) return
    void form.validateFields().then((v) => {
      const relations = ((v.relations as RelationRow[]) || [])
        .map((r) => ({
          source_entity: String(r.source_entity || '').trim(),
          relation: String(r.relation || '').trim() || '关联',
          target: String(r.target || '').trim(),
        }))
        .filter((r) => r.source_entity && r.target)

      setSaving(true)
      adminApi
        .post(
          `/admin/knowledge/items/${current.id}/relations/confirm`,
          {
            relations,
            entities: (draft?.entities || []).map((e) => ({
              name: String(e.name || '').trim(),
              type: String(e.type || '').trim(),
              properties: e.properties || {},
            })).filter((e) => e.name),
          },
          { params: scopeParams },
        )
        .then(() => {
          message.success('审核通过，已写入知识图谱')
          onDone()
        })
        .catch((e) => message.error(formatApiError(e, '审核失败')))
        .finally(() => setSaving(false))
    })
  }

  const draft = current?.relations_draft_json
  const entities = draft?.entities || []
  const entitiesWithProps = entities.filter((e) => Object.keys(e.properties || {}).length > 0)
  const entitiesEndpointOnly = entities.filter((e) => !Object.keys(e.properties || {}).length)

  return (
    <Drawer
      title={current?.title || '审核入库'}
      open={open && current != null}
      onClose={onClose}
      width={760}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button onClick={() => void discard()} loading={saving}>
            不采用
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void reextract()} loading={extracting}>
            重新抽取
          </Button>
          <Button type="primary" onClick={() => void confirm()} loading={saving}>
            审核通过
          </Button>
        </Space>
      }
    >
      {draft?.text_truncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="正文未全部参与抽取"
          description={`已分段 ${draft.segments_processed ?? draft.segments ?? '—'}/${draft.segments ?? '—'} 次；可在「抽取配置」提高「最多分段次数」或「每段正文字数」。`}
        />
      ) : null}
      {draft?.model || draft?.extracted_at ? (
        <Space size={8} wrap style={{ marginBottom: 16 }}>
          {draft.model ? <Tag>{draft.model}</Tag> : null}
          {draft.relations?.length != null ? (
            <Tag color="blue">{draft.relations.length} 条关系</Tag>
          ) : null}
          {draft.entities?.length ? (
            <Tag color="geekblue">{draft.entities.length} 个实体</Tag>
          ) : null}
          {draft.segments != null && draft.segments > 1 ? (
            <Tag>{draft.segments_processed ?? draft.segments}/{draft.segments} 段</Tag>
          ) : null}
          {draft.extracted_at ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              抽取于 {formatDateOrDash(draft.extracted_at)}
            </Typography.Text>
          ) : null}
        </Space>
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="图谱两类数据"
        description={
          <>
            <strong>实体属性</strong>：挂在某个实体上的键值（如成立时间、行业、职务），显示在下方实体卡片中。
            <br />
            <strong>实体关系</strong>：两个实体之间的连线（源实体 — 关系 — 目标实体）；目标实体是另一个业务对象，不是属性值。
          </>
        }
      />

      <Form form={form} layout="vertical" size="small">
        {entitiesWithProps.length > 0 ? (
          <Form.Item label={`实体及属性（${entitiesWithProps.length}）`} style={{ marginBottom: 16 }}>
            <div className="kg-review-entities">
              {entitiesWithProps.map((ent, idx) => {
                const props = ent.properties || {}
                return (
                  <div key={`${ent.name}-${idx}`} className="kg-review-entity-card">
                    <div className="kg-review-entity-head">
                      <Typography.Text strong>{ent.name}</Typography.Text>
                      {ent.type ? <Tag style={{ margin: 0 }}>{ent.type}</Tag> : null}
                    </div>
                    <dl className="kg-node-props">
                      {Object.entries(props).map(([k, v]) => (
                        <div key={k} className="kg-node-prop-row">
                          <dt>{k}</dt>
                          <dd>{v || '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )
              })}
            </div>
          </Form.Item>
        ) : null}

        {entitiesEndpointOnly.length > 0 ? (
          <Collapse
            size="small"
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'endpoints',
                label: `另有 ${entitiesEndpointOnly.length} 个实体未抽到属性（仅出现在关系中）`,
                children: (
                  <div className="kg-review-entities">
                    {entitiesEndpointOnly.map((ent, idx) => (
                      <div key={`${ent.name}-${idx}`} className="kg-review-entity-card">
                        <div className="kg-review-entity-head">
                          <Typography.Text>{ent.name}</Typography.Text>
                          {ent.type ? <Tag style={{ margin: 0 }}>{ent.type}</Tag> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        ) : null}

        <Form.Item
          label="实体关系"
          required
          extra="每一行表示两个实体之间的一条关系；目标实体是另一个实体名称，不是属性。"
          style={{ marginBottom: 0 }}
        >
          <Form.List name="relations">
            {(fields, { add, remove }) => (
              <>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="key"
                  dataSource={fields}
                  locale={{
                    emptyText: 'AI 未抽到关系，可手动添加后审核通过。',
                  }}
                  columns={[
                    {
                      title: '源实体',
                      width: '34%',
                      render: (_, field) => (
                        <Form.Item
                          name={[field.name, 'source_entity']}
                          rules={[{ required: true, message: '必填' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="源实体" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '关系',
                      width: '22%',
                      render: (_, field) => (
                        <Form.Item name={[field.name, 'relation']} style={{ marginBottom: 0 }}>
                          <Input placeholder="关系" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '目标实体',
                      width: '34%',
                      render: (_, field) => (
                        <Form.Item
                          name={[field.name, 'target']}
                          rules={[{ required: true, message: '必填' }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder="目标实体" />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      width: 40,
                      render: (_, field) => (
                        <MinusCircleOutlined
                          style={{ color: '#999' }}
                          onClick={() => remove(field.name)}
                        />
                      ),
                    },
                  ]}
                />
                <Button
                  type="dashed"
                  size="small"
                  style={{ marginTop: 8 }}
                  onClick={() => add({ relation: '关联' })}
                  icon={<PlusOutlined />}
                >
                  添加关系
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Drawer>
  )
}
